import { useEffect, useRef, useState } from 'react'
import { CheckCircle, Printer } from 'lucide-react'
import { toast } from 'sonner'

import type { PosOrderSuccessAction } from '@/lib/pos-order-success-tracking'
import { PosEInvoiceStatus } from '@/components/pos/pos-einvoice-status'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { useInvoiceSettings } from '@/hooks/use-invoice-query'
import {
    useOrderPackagingDraft,
    useSalesOrder,
} from '@/hooks/use-sales-order-query'
import { invoiceApi } from '@/lib/api/invoice-api'
import { m } from '@/lib/paraglide/messages'
import { capturePosOrderSuccessAction } from '@/lib/pos-order-success-tracking'
import { buildReceiptHtml, receiptDataFromOrder } from '@/lib/pos-receipt'
import { printReceiptHtml } from '@/lib/print-receipt'

interface Props {
    open: boolean
    orderId: string
    orderCode: string
    paymentMethod: 'cash' | 'bank_transfer'
    /** Thu ngân tích "Xuất hóa đơn điện tử" khi Bán ngay: tự phát hành (M-167). */
    issueEInvoice?: boolean
    /** Đơn đã xong (M-8: cả tiền mặt lẫn chuyển khoản) — đóng + dọn giỏ. */
    onDone: () => void
}

export function PosInvoicePopup({
    open,
    orderId,
    orderCode,
    paymentMethod,
    issueEInvoice = false,
    onDone,
}: Props) {
    // Pre-fetch the logo once on mount so the data URL is ready when the 100ms
    // print timer fires — avoids the race between network image load and print().
    const [logoDataUrl, setLogoDataUrl] = useState<string | undefined>(
        undefined,
    )
    useEffect(() => {
        fetch('/logo.jpg')
            .then((r) => r.blob())
            .then((blob) => {
                const reader = new FileReader()
                reader.onloadend = () => {
                    if (typeof reader.result === 'string') {
                        setLogoDataUrl(reader.result)
                    }
                }
                reader.readAsDataURL(blob)
            })
            .catch(() => {
                // Non-fatal: receipt prints without logo if the asset is unavailable.
            })
    }, [])

    // M-8: chuyển khoản xử lý y hệt tiền mặt — popup không QR, không chờ
    // webhook. M-76: đơn "Bán ngay" chờ thủ kho xuất kho — hỏi lại đơn tới khi
    // delivered để khối HĐĐT bắt kịp trạng thái (huỷ thì khoá). M-132: phát
    // hành HĐĐT không phải chờ bước xuất kho này.
    const { data: order } = useSalesOrder(orderId, { pollUntilDelivered: true })
    const { data: packagingDraft } = useOrderPackagingDraft(orderId, open)
    const { data: invoiceSettings } = useInvoiceSettings()

    function printReceipt(): boolean {
        // Print an 80mm thermal receipt client-side through the till's local
        // (USB) printer. Fall back to the A4 PDF if the data isn't loaded yet.
        if (!order || !invoiceSettings) {
            return (
                window.open(invoiceApi.getInvoicePdfUrl(orderId), '_blank') !==
                null
            )
        }
        const html = buildReceiptHtml({
            ...receiptDataFromOrder(
                order,
                invoiceSettings,
                paymentMethod,
                packagingDraft,
            ),
            logoDataUrl,
        })
        return printReceiptHtml(html)
    }

    function track(action: PosOrderSuccessAction) {
        capturePosOrderSuccessAction({ action, paymentMethod })
    }

    function handlePrint() {
        if (!printReceipt()) {
            track('print_failed')
            toast.error(m.receipt_print_failed())
            return
        }
        track('print')
        // FD-396: in xong là về màn bán mới luôn, không bắt thu ngân bấm
        // thêm "Đóng". Iframe in gắn thẳng vào body nên đóng popup không cắt
        // lệnh in đang chờ.
        onDone()
    }

    const closeButtonRef = useRef<HTMLButtonElement>(null)
    const printButtonRef = useRef<HTMLButtonElement>(null)

    function keepEnterOnPrint() {
        // M-184: Radix focus nút bấm được đầu tiên khi mở popup; đảo chỗ làm nút
        // đó thành "Đóng". Giữ Enter = In như trước (F9 Bán ngay → Enter in).
        // Khối HĐĐT có nút thì Radix vẫn focus nút đó như cũ.
        queueMicrotask(() => {
            if (document.activeElement === closeButtonRef.current) {
                printButtonRef.current?.focus()
            }
        })
    }

    return (
        <Dialog
            open={open}
            onOpenChange={() => {
                // Dấu X, Esc, bấm ra ngoài. In xong / nút chữ Đóng gọi thẳng
                // onDone nên không đi qua đây.
                track('dismiss')
                onDone()
            }}
        >
            <DialogContent
                className="max-w-sm"
                onOpenAutoFocus={keepEnterOnPrint}
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <CheckCircle className="size-5 text-green-500" />
                        {m.pos_order_success_title()}
                    </DialogTitle>
                </DialogHeader>

                <p className="text-sm font-medium">
                    {m.pos_order_code({ code: orderCode })}
                </p>

                {/* E-invoice auto-issue status */}
                <PosEInvoiceStatus
                    salesOrderID={orderId}
                    orderStatus={order?.status}
                    isQuickSale={order?.is_quick_sale}
                    autoPublish={issueEInvoice}
                />

                {/* M-184: "Đóng" trên, "In" dưới cùng theo thói quen bấm của
                    thu ngân. M-186: "In" nền xanh đậm như nút "Phát hành" để
                    nổi bật; "Đóng" giữ kiểu viền. */}
                <div className="space-y-2">
                    <Button
                        ref={closeButtonRef}
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={() => {
                            track('close_button')
                            onDone()
                        }}
                    >
                        {m.common_close()}
                    </Button>
                    <Button
                        ref={printButtonRef}
                        variant="default"
                        size="sm"
                        className="w-full"
                        onClick={handlePrint}
                    >
                        <Printer className="size-4 mr-1.5" />
                        {m.pos_print_invoice()}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    )
}
